import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  getMyKycStatus,
  getMyPayoutDetail,
  getMyPayouts,
  getMySettlementSummary,
  submitKyc,
  type KycStatusResponse,
  type PayoutDetail,
  type PayoutSummary,
  type SettlementSummaryResponse,
} from '../../api/partnerSettlement';
import type { SpringPage } from '../../api/partnerBookings';
import { useToast } from '../../components/ui/ToastProvider';

const KYC_LABELS: Record<string, string> = {
  NONE: '미제출',
  SUBMITTED: '심사 중',
  APPROVED: '승인',
  REJECTED: '반려',
};

const PAYOUT_LABELS: Record<string, string> = {
  PENDING: '대기',
  HELD: '보류(홀드)',
  READY: '지급 준비',
  REQUESTED: '지급 요청됨',
  PAID: '지급 완료',
  FAILED: '실패',
  RECONCILE_MISMATCH: '대사 불일치',
};

function payoutBadgeClass(status: string) {
  if (status === 'PAID') return 'partner-badge partner-badge--green';
  if (status === 'FAILED' || status === 'RECONCILE_MISMATCH') {
    return 'partner-badge partner-badge--red';
  }
  if (status === 'REQUESTED' || status === 'READY') return 'partner-badge partner-badge--blue';
  return 'partner-badge partner-badge--yellow';
}

function formatWon(value: number | null | undefined) {
  return value == null ? '—' : `${value.toLocaleString('ko-KR')}원`;
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${parsed.getFullYear()}.${String(parsed.getMonth() + 1).padStart(2, '0')}.${String(parsed.getDate()).padStart(2, '0')}`;
}

function errText(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function PartnerSettlementPage() {
  const { showToast } = useToast();

  const [kyc, setKyc] = useState<KycStatusResponse | null>(null);
  const [kycLoading, setKycLoading] = useState(true);
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [submittingKyc, setSubmittingKyc] = useState(false);

  const [summary, setSummary] = useState<SettlementSummaryResponse | null>(null);
  const [payouts, setPayouts] = useState<SpringPage<PayoutSummary> | null>(null);
  const [page, setPage] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [detail, setDetail] = useState<PayoutDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMyKycStatus()
      .then((data) => {
        if (cancelled) return;
        setKyc(data);
        setBankCode(data.payoutAccountBankCode ?? '');
        setAccountNumber(data.payoutAccountNumber ?? '');
        setAccountHolder(data.payoutAccountHolder ?? '');
      })
      .catch(() => {
        // 404 = 정산 정책 미생성 — 제출 폼 노출
      })
      .finally(() => {
        if (!cancelled) setKycLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPayouts = useCallback(async () => {
    setListError('');
    try {
      const [pageData, summaryData] = await Promise.all([
        getMyPayouts({ page, size: 20 }),
        getMySettlementSummary(),
      ]);
      setPayouts(pageData);
      setSummary(summaryData);
    } catch (error) {
      setListError(errText(error, '정산 내역을 불러오지 못했습니다.'));
    }
  }, [page]);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    loadPayouts().finally(() => {
      if (!cancelled) setListLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadPayouts]);

  const submitKycForm = async () => {
    if (!bankCode.trim() || !accountNumber.trim() || !accountHolder.trim()) {
      showToast('은행 코드/계좌번호/예금주를 모두 입력하세요.');
      return;
    }
    setSubmittingKyc(true);
    try {
      const result = await submitKyc({
        bankCode: bankCode.trim(),
        accountNumber: accountNumber.trim(),
        accountHolder: accountHolder.trim(),
      });
      setKyc(result);
      showToast('정산 계좌를 제출했습니다. 심사 후 승인됩니다.');
    } catch (error) {
      showToast(errText(error, '정산 계좌 제출에 실패했습니다.'));
    } finally {
      setSubmittingKyc(false);
    }
  };

  const openDetail = async (payoutId: string) => {
    setDetailLoading(true);
    try {
      const data = await getMyPayoutDetail(payoutId);
      setDetail(data);
    } catch (error) {
      showToast(errText(error, '정산 상세를 불러오지 못했습니다.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const kycStatus = kyc?.kycStatus ?? 'NONE';

  return (
    <>
      <h1 className="partner-title">정산</h1>
      <p className="partner-subtitle">정산 계좌를 등록하고 지급 내역을 확인합니다.</p>

      <div className="partner-card">
        <h2 className="partner-card__title">
          정산 계좌 (KYC)
          <span
            className={`partner-badge${
              kycStatus === 'APPROVED'
                ? ' partner-badge--green'
                : kycStatus === 'REJECTED'
                  ? ' partner-badge--red'
                  : ' partner-badge--yellow'
            }`}
          >
            {KYC_LABELS[String(kycStatus)] ?? kycStatus}
          </span>
        </h2>
        {kycLoading ? (
          <p className="partner-empty">불러오는 중…</p>
        ) : (
          <div className="partner-form">
            <div className="partner-form__row">
              <label className="partner-field">
                <span className="partner-field__label">
                  은행 코드 <small>(예: 088 신한)</small>
                </span>
                <input
                  onChange={(event) => setBankCode(event.target.value)}
                  type="text"
                  value={bankCode}
                />
              </label>
              <label className="partner-field">
                <span className="partner-field__label">계좌번호</span>
                <input
                  onChange={(event) => setAccountNumber(event.target.value)}
                  type="text"
                  value={accountNumber}
                />
              </label>
              <label className="partner-field">
                <span className="partner-field__label">예금주</span>
                <input
                  onChange={(event) => setAccountHolder(event.target.value)}
                  type="text"
                  value={accountHolder}
                />
              </label>
            </div>
            <div className="partner-form__actions">
              <button
                className="partner-btn"
                disabled={submittingKyc}
                onClick={submitKycForm}
                type="button"
              >
                {submittingKyc ? '제출 중…' : kyc ? '계좌 재제출' : '계좌 제출'}
              </button>
            </div>
          </div>
        )}
      </div>

      {summary ? (
        <div className="partner-card">
          <h2 className="partner-card__title">정산 요약</h2>
          <div className="partner-stats-grid">
            <div className="partner-stat">
              <div className="partner-stat__label">총 결제액</div>
              <div className="partner-stat__value">{formatWon(summary.totalPaymentAmountWon)}</div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">플랫폼 수수료</div>
              <div className="partner-stat__value">{formatWon(summary.totalPlatformFeeWon)}</div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">지급 예정액</div>
              <div className="partner-stat__value">
                {formatWon(summary.totalExpectedPayoutWon)}
              </div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">지급 완료액</div>
              <div className="partner-stat__value">
                {formatWon(summary.totalConfirmedPayoutWon)}
              </div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">지급 완료 건</div>
              <div className="partner-stat__value">{summary.paidPayoutCount}건</div>
            </div>
            <div className="partner-stat">
              <div className="partner-stat__label">대기/보류 건</div>
              <div className="partner-stat__value">{summary.pendingPayoutCount}건</div>
            </div>
          </div>
        </div>
      ) : null}

      {listError ? <div className="partner-error">{listError}</div> : null}

      <div className="partner-card">
        <h2 className="partner-card__title">지급 내역</h2>
        {listLoading ? (
          <p className="partner-empty">불러오는 중…</p>
        ) : !payouts || payouts.content.length === 0 ? (
          <p className="partner-empty">정산 내역이 없습니다.</p>
        ) : (
          <>
            <div className="partner-table-wrap">
              <table className="partner-table">
                <thead>
                  <tr>
                    <th>정산 ID</th>
                    <th>결제액</th>
                    <th>수수료</th>
                    <th>지급 예정액</th>
                    <th>홀드 해제일</th>
                    <th>지급일</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.content.map((payout) => (
                    <tr key={payout.id}>
                      <td>
                        <button
                          className="partner-btn partner-btn--ghost partner-btn--sm"
                          onClick={() => openDetail(payout.id)}
                          type="button"
                        >
                          {payout.id}
                        </button>
                      </td>
                      <td>{formatWon(payout.paymentAmountWon)}</td>
                      <td>{formatWon(payout.platformFeeWon)}</td>
                      <td>{formatWon(payout.expectedPayoutWon ?? payout.vendorPayoutWon)}</td>
                      <td>{formatDate(payout.holdUntilAt)}</td>
                      <td>{formatDate(payout.paidAt)}</td>
                      <td>
                        <span className={payoutBadgeClass(String(payout.status))}>
                          {PAYOUT_LABELS[String(payout.status)] ?? payout.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="partner-pagination">
              <button
                className="partner-btn partner-btn--ghost partner-btn--sm"
                disabled={payouts.first}
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                type="button"
              >
                이전
              </button>
              <span>
                {payouts.number + 1} / {Math.max(1, payouts.totalPages)} 페이지 · 총{' '}
                {payouts.totalElements}건
              </span>
              <button
                className="partner-btn partner-btn--ghost partner-btn--sm"
                disabled={payouts.last}
                onClick={() => setPage((prev) => prev + 1)}
                type="button"
              >
                다음
              </button>
            </div>
          </>
        )}
      </div>

      {detailLoading || detail ? (
        <div className="partner-card">
          <h2 className="partner-card__title">
            정산 상세
            {detail ? (
              <button
                className="partner-btn partner-btn--ghost partner-btn--sm"
                onClick={() => setDetail(null)}
                type="button"
              >
                닫기
              </button>
            ) : null}
          </h2>
          {detailLoading ? (
            <p className="partner-empty">불러오는 중…</p>
          ) : detail ? (
            <dl className="partner-detail-grid">
              <div>
                <dt>정산 ID</dt>
                <dd>{detail.id}</dd>
              </div>
              <div>
                <dt>예약 ID</dt>
                <dd>{detail.reservationId ?? '—'}</dd>
              </div>
              <div>
                <dt>등급 / 마진율</dt>
                <dd>
                  {detail.gradeCode ?? '—'} /{' '}
                  {detail.marginRateBps != null ? `${detail.marginRateBps / 100}%` : '—'}
                </dd>
              </div>
              <div>
                <dt>정가</dt>
                <dd>{formatWon(detail.listPriceWon)}</dd>
              </div>
              <div>
                <dt>결제액</dt>
                <dd>{formatWon(detail.paymentAmountWon)}</dd>
              </div>
              <div>
                <dt>벤더 쿠폰 할인</dt>
                <dd>{formatWon(detail.vendorCouponDiscountWon)}</dd>
              </div>
              <div>
                <dt>플랫폼 쿠폰 할인</dt>
                <dd>{formatWon(detail.platformCouponDiscountWon)}</dd>
              </div>
              <div>
                <dt>플랫폼 수수료</dt>
                <dd>{formatWon(detail.platformFeeWon)}</dd>
              </div>
              <div>
                <dt>지급 예정액</dt>
                <dd>{formatWon(detail.expectedPayoutWon)}</dd>
              </div>
              <div>
                <dt>확정 지급액</dt>
                <dd>{formatWon(detail.confirmedPayoutWon)}</dd>
              </div>
              <div>
                <dt>상태</dt>
                <dd>
                  <span className={payoutBadgeClass(String(detail.status))}>
                    {PAYOUT_LABELS[String(detail.status)] ?? detail.status}
                  </span>
                </dd>
              </div>
              {detail.failureReason ? (
                <div>
                  <dt>실패 사유</dt>
                  <dd>{detail.failureReason}</dd>
                </div>
              ) : null}
            </dl>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
